// src/components/InviteUsers.js
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../axiosConfig';
import { toast } from 'react-toastify';
import QRCode from 'qrcode';
import { formatDateTime } from '../utils/dateTimeFormatter';
import { Search, Filter, Users, AlertTriangle } from 'lucide-react';
import { ROLE_HIERARCHY, getRoleDisplayName, getRoleLevel, hasRoleLevel } from '../utils/roleUtils';
import { useTheme } from '../contexts/ThemeContext';
import { isInternationalNumber } from '../utils/phoneUtils';
import ConfirmDialogShell from './dialogs/ConfirmDialogShell';

/**
 * InviteUsers Component
 * Allows admins to send user invitations and manage pending invitations
 * Implements role-based invitation permissions
 */
const InviteUsers = ({ currentEntity, userRole }) => {
  const { t } = useTranslation();
  const { text, cn } = useTheme();

  // State for invitation form
  const [inviteForm, setInviteForm] = useState({
    contactMethod: 'email', // 'email', 'sms', 'whatsapp', 'telegram', 'wechat'
    contactValue: '', // email address, phone number, username, etc.
    role: 'user',
    invitationType: 'join_entity',
    invitationMessage: '',
    expiryDays: 7,
    relationshipType: 'general', // 'general' or 'related'
    relationType: 'guardian' // for related invitations
  });

  // Contact method options
  const contactMethods = [
    { value: 'email', label: t('contactMethodEmail'), enabled: true },
    { value: 'sms', label: t('contactMethodSMS'), enabled: true },
    { value: 'whatsapp', label: t('contactMethodWhatsApp'), enabled: true },
    { value: 'telegram', label: t('contactMethodTelegram'), enabled: false },
    { value: 'wechat', label: t('contactMethodWeChat'), enabled: false }
  ];

  // State for invitations list
  const [invitations, setInvitations] = useState([]);
  const [filteredInvitations, setFilteredInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(true);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [pendingCancelInviteId, setPendingCancelInviteId] = useState(null);
  const [cancellingInvite, setCancellingInvite] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCreatedBy, setFilterCreatedBy] = useState('all'); // all, me, others
  const [dateFilter, setDateFilter] = useState('all');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  // QR Code state
  const [showQRCodeForm, setShowQRCodeForm] = useState(false);
  const [generatedQRCodes, setGeneratedQRCodes] = useState([]);
  const [qrCodeForm, setQRCodeForm] = useState({
    role: 'user',
    description: '',
    sourceLocation: '',
    maxUses: '',
    expiryDays: 365
  });
  const [generatingQR, setGeneratingQR] = useState(false);
  // Role hierarchy is now imported from roleUtils

  // Available relation types for user relationships
  const relationTypes = [
    { value: 'guardian', label: t('guardian') },
    { value: 'parent', label: t('parent') },
    { value: 'grandparent', label: t('grandparent') },
    { value: 'sibling', label: t('sibling') },
    { value: 'other', label: t('other') }
  ];

  // Status options for filtering
  const statusOptions = [
    { value: 'pending', label: t('pending') },
    { value: 'accepted', label: t('accepted') },
    { value: 'expired', label: t('expired') },
    { value: 'cancelled', label: t('cancelled') }
  ];

  // Get manageable roles based on current user's role
  const getManageableRoles = () => {
    if (hasRoleLevel(userRole, 'super_admin')) {
      return ROLE_HIERARCHY;
    }
    
    const currentRoleLevel = getRoleLevel(userRole);
    if (currentRoleLevel === -1) {
      return ['user']; // Fallback for unknown roles
    }
    
    // Users can assign roles at their level or below
    return ROLE_HIERARCHY.slice(0, currentRoleLevel + 1);
  };

  const manageableRoles = getManageableRoles();

  // Role-based filtering is now handled by the backend

  /**
   * Get current user's email for filtering own invitations
   */
  const getCurrentUserEmail = () => {
    // This should come from user context or props
    // For now, we'll try to get it from localStorage or make an API call
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    return userData.email || '';
  };

  /**
   * Filter invitations based on search and filter criteria
   */
  const filterInvitations = () => {
    let filtered = [...invitations];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(invitation => {
        const contactValue = invitation.contactValue || invitation.email || '';
        const firstName = invitation.inviterFirstName || '';
        const lastName = invitation.inviterLastName || '';

        return contactValue.toLowerCase().includes(searchLower) ||
               firstName.toLowerCase().includes(searchLower) ||
               lastName.toLowerCase().includes(searchLower);
      });
    }

    // Role filter
    if (filterRole !== 'all') {
      filtered = filtered.filter(invitation => invitation.role === filterRole);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(invitation => invitation.status === filterStatus);
    }

    // Created by filter
    if (filterCreatedBy !== 'all') {
      const currentUserEmail = getCurrentUserEmail();
      if (filterCreatedBy === 'me') {
        filtered = filtered.filter(invitation => invitation.inviterEmail === currentUserEmail);
      } else if (filterCreatedBy === 'others') {
        filtered = filtered.filter(invitation => invitation.inviterEmail !== currentUserEmail);
      }
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let startDate, endDate;
      
      switch (dateFilter) {
        case 'last7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'last30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'last90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case 'custom':
          if (dateStart) startDate = new Date(dateStart);
          if (dateEnd) endDate = new Date(dateEnd + 'T23:59:59');
          break;
        default:
          break;
      }
      
      if (startDate || endDate) {
        filtered = filtered.filter(invitation => {
          const invitationDate = new Date(invitation.createdAt);
          if (startDate && endDate) {
            return invitationDate >= startDate && invitationDate <= endDate;
          } else if (startDate) {
            return invitationDate >= startDate;
          } else if (endDate) {
            return invitationDate <= endDate;
          }
          return true;
        });
      }
    }

    setFilteredInvitations(filtered);
  };

  // Load invitations when component mounts
  useEffect(() => {
    if (currentEntity?.entityId || currentEntity?.id) {
      loadInvitations(); // Re-enabled - backend endpoint should be ready
    } else {
      // If no entity, set loading to false to prevent infinite spinner
      setLoadingInvitations(false);
    }
  }, [currentEntity]);

  // Filter invitations when filters change
  useEffect(() => {
    filterInvitations();
  }, [invitations, searchTerm, filterRole, filterStatus, filterCreatedBy, dateFilter, dateStart, dateEnd]);

  /**
   * Load pending invitations for the current entity
   */
  const loadInvitations = async () => {
    try {
      setLoadingInvitations(true);
      const entityId = currentEntity.entityId || currentEntity.id;
      const response = await axios.get(`/api/entities/${entityId}/invites?status=all&userRole=${userRole}`);
      
      if (response.data.success) {
        setInvitations(response.data.data);
      } else {
        toast.error(t('Failed to load invitations'));
      }
    } catch (error) {
      console.error('Error loading invitations', error, { entityId: currentEntity?.entityId || currentEntity?.id });
      toast.error(t('Error loading invitations'));
    } finally {
      setLoadingInvitations(false);
    }
  };

  /**
   * Handle invite form field changes
   */
  const handleInviteFormChange = (e) => {
    const { name, value } = e.target;
    setInviteForm(prev => {
      const newForm = {
        ...prev,
        [name]: value
      };

      // Reset relationship fields when switching invitation types
      if (name === 'invitationType') {
        if (value === 'create_entity') {
          // Clear relationship fields for new organization
          newForm.relationshipType = 'general';
          newForm.relationType = 'guardian';
        } else if (value === 'join_entity') {
          // Ensure relationship fields have defaults when switching back
          newForm.relationshipType = newForm.relationshipType || 'general';
          newForm.relationType = newForm.relationType || 'guardian';
        }
      }

      return newForm;
    });

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * Validate invitation form
   */
  const validateInviteForm = () => {
    const errors = {};

    // Contact method validation
    if (!inviteForm.contactMethod) {
      errors.contactMethod = t('contactMethodRequired');
    }

    // Contact value validation based on method
    if (!inviteForm.contactValue.trim()) {
      errors.contactValue = t('invalidContactValue');
    } else {
      // Email validation
      if (inviteForm.contactMethod === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(inviteForm.contactValue)) {
          errors.contactValue = t('validation.emailInvalidFormat');
        }
      }
      // Phone validation for SMS and WhatsApp
      else if (inviteForm.contactMethod === 'sms' || inviteForm.contactMethod === 'whatsapp') {
        const phoneRegex = /^\+?[1-9]\d{1,14}$/;
        const cleanedPhone = inviteForm.contactValue.replace(/[\s-()]/g, '');
        if (!phoneRegex.test(cleanedPhone)) {
          errors.contactValue = t('invalidPhoneFormat');
        }
      }
    }

    // Role validation
    if (!inviteForm.role) {
      errors.role = t('validation.roleRequired');
    } else if (!manageableRoles.includes(inviteForm.role)) {
      errors.role = t('validation.cannotAssignRole');
    }

    // Expiry days validation
    if (inviteForm.expiryDays < 1 || inviteForm.expiryDays > 30) {
      errors.expiryDays = t('validation.expiryDaysRange');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Send invitation
   */
  const sendInvitation = async (e) => {
    e.preventDefault();
    
    if (!validateInviteForm()) {
      toast.error(t('Please fix the form errors'));
      return;
    }

    setSendingInvite(true);

    try {
      const entityId = currentEntity.entityId || currentEntity.id;
      const trimmedContact = inviteForm.contactValue.trim();

      // Policy-aware hard block for phone-based invitations (SMS/WhatsApp both route to SMS backend channel).
      if (inviteForm.contactMethod === 'sms' || inviteForm.contactMethod === 'whatsapp') {
        const checkResponse = await axios.get(`/api/notifications/${entityId}/sms/check-phone`, {
          params: { phone: trimmedContact }
        });
        const checkData = checkResponse?.data?.data;
        if (!checkData?.allowed) {
          const policyMessage = checkData.reason === 'international_blocked'
            ? t('smsInternationalBlocked')
            : (checkData.reason === 'invalid_phone' ? t('invalidPhoneFormat') : t('smsDisabledBySystem'));
          toast.error(policyMessage);
          return;
        }
      }

      const inviteData = {
        contactMethod: inviteForm.contactMethod,
        contactValue: trimmedContact,
        role: inviteForm.role,
        invitationType: inviteForm.invitationType,
        invitationMessage: inviteForm.invitationMessage.trim(),
        expiryDays: parseInt(inviteForm.expiryDays),
        // Only include relationshipType and relationType for join_entity invitations
        ...(inviteForm.invitationType === 'join_entity' && {
          relationshipType: inviteForm.relationshipType,
          relationType: inviteForm.relationshipType === 'related' ? inviteForm.relationType : null
        })
        // entityName and entityData not sent - backend should handle undefined
      };
      console.log("Sending invitation", inviteData);
      const response = await axios.post(`/api/entities/${entityId}/invite`, inviteData);

      if (response.data.success) {
        const contactDisplay = inviteForm.contactMethod === 'email'
          ? inviteForm.contactValue
          : `${inviteForm.contactMethod}: ${inviteForm.contactValue}`;
        toast.success(t(`Invitation sent to ${contactDisplay}`));

        // Reset form
        setInviteForm({
          contactMethod: 'email',
          contactValue: '',
          role: 'user',
          invitationType: 'join_entity',
          invitationMessage: '',
          expiryDays: 7,
          relationshipType: 'general',
          relationType: 'guardian'
        });
        
        setShowInviteForm(false);
        loadInvitations(); // Reload invitations list
      } else {
        toast.error(response.data.error || t('Failed to send invitation'));
      }
    } catch (error) {
      console.error('Error sending invitation', error, {
        entityId: currentEntity?.entityId || currentEntity?.id,
        contactMethod: inviteForm.contactMethod,
        contactValue: inviteForm.contactValue
      });

      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(t('Error sending invitation'));
      }
    } finally {
      setSendingInvite(false);
    }
  };

  /**
   * Cancel invitation
   */
  const cancelInvitation = (inviteId) => {
    setPendingCancelInviteId(inviteId);
  };

  const confirmCancelInvitation = async () => {
    if (!pendingCancelInviteId) return;
    try {
      setCancellingInvite(true);
      const response = await axios.delete(`/api/entities/invites/${pendingCancelInviteId}`);
      
      if (response.data.success) {
        toast.success(t('Invitation cancelled'));
        setPendingCancelInviteId(null);
        loadInvitations(); // Reload invitations list
      } else {
        toast.error(t('Failed to cancel invitation'));
      }
    } catch (error) {
      console.error('Error cancelling invitation', error, { inviteId: pendingCancelInviteId });
      toast.error(t('Error cancelling invitation'));
    } finally {
      setCancellingInvite(false);
    }
  };

  /**
   * Resend invitation
   */
  const resendInvitation = async (inviteId) => {
    try {
      const response = await axios.post(`/api/entities/invites/${inviteId}/resend`);
      
      if (response.data.success) {
        toast.success(t('Invitation resent successfully'));
      } else {
        toast.error(response.data.error || t('Failed to resend invitation'));
      }
    } catch (error) {
      console.error('Error resending invitation', error, { inviteId });
      toast.error(t('Error resending invitation'));
    }
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      accepted: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      expired: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    };

    return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`;
  };


  /**
   * Handle QR code form field changes
   */
  const handleQRCodeFormChange = (e) => {
    const { name, value } = e.target;
    setQRCodeForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Generate QR code invitation
   */
  const generateQRCode = async (e) => {
    e.preventDefault();
    
    if (!qrCodeForm.description.trim()) {
      toast.error(t('validation.descriptionRequiredForQR'));
      return;
    }

    setGeneratingQR(true);

    try {
      const entityId = currentEntity.entityId || currentEntity.id;
      const qrData = {
        role: qrCodeForm.role,
        description: qrCodeForm.description.trim(),
        sourceLocation: qrCodeForm.sourceLocation.trim() || null,
        maxUses: qrCodeForm.maxUses ? parseInt(qrCodeForm.maxUses) : null,
        expiryDays: parseInt(qrCodeForm.expiryDays)
      };

      const response = await axios.post(`/api/entities/${entityId}/invites/generic`, qrData);
      
      if (response.data.success) {
        const { inviteToken, description, sourceLocation, maxUses, expiresAt } = response.data.data;
        
        // Generate QR code image
        const qrCodeUrl = `${window.location.origin}/register/${inviteToken}`;
        const qrCodeDataURL = await QRCode.toDataURL(qrCodeUrl, {
          errorCorrectionLevel: 'M',
          type: 'image/png',
          quality: 0.92,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          width: 256
        });

        // Add to generated QR codes list
        const newQRCode = {
          id: Date.now(),
          token: inviteToken,
          description,
          sourceLocation,
          maxUses,
          expiresAt,
          role: qrData.role,
          qrCodeDataURL,
          qrCodeUrl,
          usageCount: 0
        };

        setGeneratedQRCodes(prev => [newQRCode, ...prev]);
        
        // Reset form
        setQRCodeForm({
          role: 'user',
          description: '',
          sourceLocation: '',
          maxUses: '',
          expiryDays: 365
        });

        toast.success(t('QR code generated successfully!'));
      } else {
        toast.error(t('Failed to generate QR code'));
      }
    } catch (error) {
      console.error('Error generating QR code', error, { 
        entityId: currentEntity?.entityId || currentEntity?.id, 
        description: qrCodeForm.description 
      });
      toast.error(t('Error generating QR code'));
    } finally {
      setGeneratingQR(false);
    }
  };

  /**
   * Download QR code as PNG
   */
  const downloadQRCode = (qrCode) => {
    const link = document.createElement('a');
    link.download = `QRCode-${qrCode.description.replace(/[^a-zA-Z0-9]/g, '_')}.png`;
    link.href = qrCode.qrCodeDataURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(t('QR code downloaded!'));
  };

  /**
   * Copy QR code URL to clipboard
   */
  const copyQRCodeURL = (qrCode) => {
    navigator.clipboard.writeText(qrCode.qrCodeUrl).then(() => {
      toast.success(t('QR code URL copied to clipboard!'));
    }).catch(() => {
      toast.error(t('Failed to copy URL'));
    });
  };

  /**
   * Print QR code
   */
  const printQRCode = (qrCode) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>QR Code - ${qrCode.description}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              text-align: center; 
              padding: 20px; 
            }
            .qr-container { 
              margin: 20px auto; 
              max-width: 400px; 
            }
            .qr-code { 
              max-width: 100%; 
              height: auto; 
            }
            .info { 
              margin: 10px 0; 
              font-size: 14px; 
            }
            .title { 
              font-size: 18px; 
              font-weight: bold; 
              margin-bottom: 10px; 
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="title">${qrCode.description}</div>
            <img src="${qrCode.qrCodeDataURL}" alt="QR Code" class="qr-code" />
            <div class="info">
              <div>Role: ${qrCode.role}</div>
              ${qrCode.sourceLocation ? `<div>Location: ${qrCode.sourceLocation}</div>` : ''}
              ${qrCode.maxUses ? `<div>Max Uses: ${qrCode.maxUses}</div>` : '<div>Unlimited Uses</div>'}
              <div>Expires: ${formatDateTime(qrCode.expiresAt)}</div>
              <div style="margin-top: 10px; font-size: 12px; color: #666;">
                ${t('scanThisQRCodeToJoin')} ${currentEntity?.entityName || t('thisOrganization')}
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Users className="w-5 h-5 text-gray-600" />
          <span className="text-lg font-medium text-gray-700">
            {t('inviteUsers')} ({filteredInvitations.length}/{invitations.length})
          </span>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowQRCodeForm(!showQRCodeForm)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h4" />
            </svg>
            {t('generateQRCode')}
          </button>
          <button
            onClick={() => setShowInviteForm(!showInviteForm)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {t('sendNewInvitation')}
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Search className="inline w-4 h-4 mr-1" />
              {t('search')}
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('searchInvitations')}
              className={cn(
                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-700",
                text.primary,
                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
              )}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Filter className="inline w-4 h-4 mr-1" />
              {t('filterByRole')}
            </label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className={cn(
                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-700",
                text.primary,
                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
              )}
            >
              <option value="all">{t('allRoles')}</option>
              {manageableRoles.map(role => (
                <option key={role} value={role}>
                  {t(getRoleDisplayName(role))}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Filter className="inline w-4 h-4 mr-1" />
              {t('filterByStatus')}
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className={cn(
                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-700",
                text.primary,
                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
              )}
            >
              <option value="all">{t('allStatuses')}</option>
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Filter className="inline w-4 h-4 mr-1" />
              {t('createdBy')}
            </label>
            <select
              value={filterCreatedBy}
              onChange={(e) => setFilterCreatedBy(e.target.value)}
              className={cn(
                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-700",
                text.primary,
                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
              )}
            >
              <option value="all">{t('allCreators')}</option>
              <option value="me">{t('myInvitations')}</option>
              <option value="others">{t('otherInvitations')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Filter className="inline w-4 h-4 mr-1" />
              {t('filterByDate')}
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className={cn(
                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-700",
                text.primary,
                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
              )}
            >
              <option value="all">{t('allDates')}</option>
              <option value="last7days">{t('last7Days')}</option>
              <option value="last30days">{t('last30Days')}</option>
              <option value="last90days">{t('last90Days')}</option>
              <option value="custom">{t('customRange')}</option>
            </select>
          </div>
        </div>
        
        {/* Custom Date Range */}
        {dateFilter === 'custom' && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('startDate')}
              </label>
              <input
                type="date"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                className={cn(
                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-700",
                text.primary,
                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
              )}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('endDate')}
              </label>
              <input
                type="date"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                className={cn(
                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-700",
                text.primary,
                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
              )}
              />
            </div>
          </div>
        )}
      </div>

      {/* Invitation Form */}
      {showInviteForm && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('sendNewInvitation')}</h3>
          
          <form onSubmit={sendInvitation} className="space-y-4">
            {/* Contact Method */}
            <div>
              <label htmlFor="contactMethod" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('inviteContactMethod')} *
              </label>
              <select
                id="contactMethod"
                name="contactMethod"
                required
                value={inviteForm.contactMethod}
                onChange={handleInviteFormChange}
                className={`mt-1 block w-full px-3 py-2 border ${
                  validationErrors.contactMethod ? 'border-red-300' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
              >
                {contactMethods.filter(m => m.enabled).map(method => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
              {validationErrors.contactMethod && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.contactMethod}</p>
              )}
            </div>

            {/* Contact Value (dynamic based on method) */}
            <div>
              <label htmlFor="contactValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {inviteForm.contactMethod === 'email' && t('emailAddress')}
                {(inviteForm.contactMethod === 'sms' || inviteForm.contactMethod === 'whatsapp') && t('Phone Number')}
                {(inviteForm.contactMethod === 'telegram' || inviteForm.contactMethod === 'wechat') && t('Username')}
                {' *'}
              </label>
              <input
                type={inviteForm.contactMethod === 'email' ? 'email' : 'text'}
                id="contactValue"
                name="contactValue"
                required
                value={inviteForm.contactValue}
                onChange={handleInviteFormChange}
                className={`mt-1 block w-full px-3 py-2 border ${
                  validationErrors.contactValue ? 'border-red-300' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                placeholder={
                  inviteForm.contactMethod === 'email' ? t('enterEmail') :
                  (inviteForm.contactMethod === 'sms' || inviteForm.contactMethod === 'whatsapp') ? t('enterPhone') :
                  t('enterUsername')
                }
              />
              {(inviteForm.contactMethod === 'sms' || inviteForm.contactMethod === 'whatsapp') && !validationErrors.contactValue && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('phoneNumberHelperText')}</p>
              )}
              {validationErrors.contactValue && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.contactValue}</p>
              )}
              {/* Hard block: SMS/WhatsApp to international number */}
              {(inviteForm.contactMethod === 'sms' || inviteForm.contactMethod === 'whatsapp') &&
                inviteForm.contactValue && isInternationalNumber(inviteForm.contactValue) &&
                !validationErrors.contactValue && (
                <div className="mt-1 flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{t('smsInternationalBlocked')}</span>
                </div>
              )}
            </div>

            {/* Role */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('role')} *
              </label>
              <select
                id="role"
                name="role"
                required
                value={inviteForm.role}
                onChange={handleInviteFormChange}
                className={`mt-1 block w-full px-3 py-2 border ${
                  validationErrors.role ? 'border-red-300' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
              >
                {manageableRoles.map(role => (
                  <option key={role} value={role}>
                    {t(getRoleDisplayName(role))}
                  </option>
                ))}
              </select>
              {validationErrors.role && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.role}</p>
              )}
            </div>

            {/* Invitation Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('invitationType')} *
              </label>
              <div className="mt-2 space-y-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="invitationType"
                    value="join_entity"
                    checked={inviteForm.invitationType === 'join_entity'}
                    onChange={handleInviteFormChange}
                    className="form-radio h-4 w-4 text-blue-600 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    {t('registerForShuttleServicesBy')} ({currentEntity?.entityName})
                  </span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="invitationType"
                    value="create_entity"
                    checked={inviteForm.invitationType === 'create_entity'}
                    onChange={handleInviteFormChange}
                    className="form-radio h-4 w-4 text-blue-600 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    {t('createNewOrganization')}
                  </span>
                </label>
              </div>
            </div>

            {/* Relationship Type - Only shown when inviting to same entity */}
            {inviteForm.invitationType === 'join_entity' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('relationshipType')} *
                </label>
                <div className="mt-2 space-y-2">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="relationshipType"
                      value="general"
                      checked={inviteForm.relationshipType === 'general'}
                      onChange={handleInviteFormChange}
                      className="form-radio h-4 w-4 text-blue-600 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      {t('generalUser')} - {t('noRelationshipToYou')}
                    </span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="relationshipType"
                      value="related"
                      checked={inviteForm.relationshipType === 'related'}
                      onChange={handleInviteFormChange}
                      className="form-radio h-4 w-4 text-blue-600 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      {t('relatedPerson')} - {t('familyMemberOrRelative')}
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Relation Type (shown only for related invitations) */}
            {inviteForm.relationshipType === 'related' && (
              <div>
                <label htmlFor="relationType" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('specificRelation')} *
                </label>
                <select
                  id="relationType"
                  name="relationType"
                  value={inviteForm.relationType}
                  onChange={handleInviteFormChange}
                  className={cn(
                  "mt-1 block w-full px-3 py-2 border rounded-md shadow-sm transition-all duration-200 sm:text-sm",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700",
                  text.primary,
                  "focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                  "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                )}
                >
                  {relationTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('thisWillCreateARelationshipRecord')}
                </p>
              </div>
            )}


            {/* Invitation Message */}
            <div>
              <label htmlFor="invitationMessage" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('personalMessage')} ({t('optional')})
              </label>
              <textarea
                id="invitationMessage"
                name="invitationMessage"
                rows={3}
                value={inviteForm.invitationMessage}
                onChange={handleInviteFormChange}
                className={cn(
                  "mt-1 block w-full px-3 py-2 border rounded-md shadow-sm transition-all duration-200 sm:text-sm",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700",
                  text.primary,
                  "focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                  "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                )}
                placeholder={t('invitationMessagePlaceholder')}
              />
            </div>

            {/* Expiry Days */}
            <div>
              <label htmlFor="expiryDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('expiresIn')} ({t('days')}) *
              </label>
              <select
                id="expiryDays"
                name="expiryDays"
                value={inviteForm.expiryDays}
                onChange={handleInviteFormChange}
                className={cn(
                  "mt-1 block w-full px-3 py-2 border rounded-md shadow-sm transition-all duration-200 sm:text-sm",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700",
                  text.primary,
                  "focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                  "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                )}
              >
                <option value={1}>1 {t('day')}</option>
                <option value={3}>3 {t('days')}</option>
                <option value={7}>7 {t('days')}</option>
                <option value={14}>14 {t('days')}</option>
                <option value={30}>30 {t('days')}</option>
              </select>
              {validationErrors.expiryDays && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.expiryDays}</p>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={() => setShowInviteForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={sendingInvite}
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  sendingInvite
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {sendingInvite ? t('sending') : t('sendInvitation')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* QR Code Generation Form */}
      {showQRCodeForm && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('generateQRCodeInvitation')}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {t('createQRCodeInvitation')}
          </p>
          
          <form onSubmit={generateQRCode} className="space-y-4">
            {/* Description */}
            <div>
              <label htmlFor="qr-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('description')} *
              </label>
              <input
                type="text"
                id="qr-description"
                name="description"
                required
                value={qrCodeForm.description}
                onChange={handleQRCodeFormChange}
                className={cn(
                  "mt-1 block w-full px-3 py-2 border rounded-md shadow-sm transition-all duration-200 sm:text-sm",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700",
                  text.primary,
                  "focus:outline-none focus:ring-green-500 focus:border-green-500 focus:ring-2",
                  "hover:border-green-400 dark:hover:border-green-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                )}
                placeholder={t('qrCodeDescriptionPlaceholder')}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('thisWillHelpYouTrackWhichQRCodeAreMostEffective')}
              </p>
            </div>

            {/* Source Location */}
            <div>
              <label htmlFor="qr-sourceLocation" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('location')} ({t('optional')})
              </label>
              <input
                type="text"
                id="qr-sourceLocation"
                name="sourceLocation"
                value={qrCodeForm.sourceLocation}
                onChange={handleQRCodeFormChange}
                className={cn(
                  "mt-1 block w-full px-3 py-2 border rounded-md shadow-sm transition-all duration-200 sm:text-sm",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700",
                  text.primary,
                  "focus:outline-none focus:ring-green-500 focus:border-green-500 focus:ring-2",
                  "hover:border-green-400 dark:hover:border-green-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                )}
                placeholder={t('sourceLocationPlaceholder')}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Role */}
              <div>
                <label htmlFor="qr-role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('defaultRole')} *
                </label>
                <select
                  id="qr-role"
                  name="role"
                  value={qrCodeForm.role}
                  onChange={handleQRCodeFormChange}
                  className={cn(
                  "mt-1 block w-full px-3 py-2 border rounded-md shadow-sm transition-all duration-200 sm:text-sm",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700",
                  text.primary,
                  "focus:outline-none focus:ring-green-500 focus:border-green-500 focus:ring-2",
                  "hover:border-green-400 dark:hover:border-green-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                )}
                >
                  {manageableRoles.map(role => (
                    <option key={role} value={role}>
                      {t(getRoleDisplayName(role))}
                    </option>
                  ))}
                </select>
              </div>

              {/* Max Uses */}
              <div>
                <label htmlFor="qr-maxUses" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('maxUses')}
                </label>
                <input
                  type="number"
                  id="qr-maxUses"
                  name="maxUses"
                  min="1"
                  value={qrCodeForm.maxUses}
                  onChange={handleQRCodeFormChange}
                  className={cn(
                  "mt-1 block w-full px-3 py-2 border rounded-md shadow-sm transition-all duration-200 sm:text-sm",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700",
                  text.primary,
                  "focus:outline-none focus:ring-green-500 focus:border-green-500 focus:ring-2",
                  "hover:border-green-400 dark:hover:border-green-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                )}
                  placeholder={t('unlimitedPlaceholder')}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('leaveBlankForUnlimitedUses')}
                </p>
              </div>

              {/* Expiry Days */}
              <div>
                <label htmlFor="qr-expiryDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('expires')} *
                </label>
                <select
                  id="qr-expiryDays"
                  name="expiryDays"
                  value={qrCodeForm.expiryDays}
                  onChange={handleQRCodeFormChange}
                  className={cn(
                  "mt-1 block w-full px-3 py-2 border rounded-md shadow-sm transition-all duration-200 sm:text-sm",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-700",
                  text.primary,
                  "focus:outline-none focus:ring-green-500 focus:border-green-500 focus:ring-2",
                  "hover:border-green-400 dark:hover:border-green-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                )}
                >
                  <option value={30}>30 {t('days')}</option>
                  <option value={90}>90 {t('days')}</option>
                  <option value={180}>6 {t('months')}</option>
                  <option value={365}>1 {t('year')}</option>
                  <option value={730}>2 {t('years')}</option>
                </select>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={() => setShowQRCodeForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={generatingQR}
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  generatingQR
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                }`}
              >
                {generatingQR ? t('generating') : t('generateQRCode')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Generated QR Codes */}
      {generatedQRCodes.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('generatedQRCodes')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('downloadPrintOrCopyLinksForYourQRCodes')}
            </p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {generatedQRCodes.map((qrCode) => (
                <div key={qrCode.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  {/* QR Code Image */}
                  <div className="text-center mb-4">
                    <img 
                      src={qrCode.qrCodeDataURL} 
                      alt={`QR Code: ${qrCode.description}`}
                      className="mx-auto w-48 h-48 border border-gray-200 dark:border-gray-600 rounded"
                    />
                  </div>
                  
                  {/* QR Code Info */}
                  <div className="space-y-2 text-sm">
                    <div className="font-medium text-gray-900 dark:text-white">{qrCode.description}</div>
                    {qrCode.sourceLocation && (
                      <div className="text-gray-600 dark:text-gray-400">📍 {qrCode.sourceLocation}</div>
                    )}
                    <div className="text-gray-600 dark:text-gray-400">
                      {t('role')}: <span className="font-medium">{t(getRoleDisplayName(qrCode.role))}</span>
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">
                      {qrCode.maxUses ? `${t('max')}: ${qrCode.maxUses} ${t('uses')}` : t('unlimitedUses')}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">
                      {t('expires')}: {formatDateTime(qrCode.expiresAt)}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => downloadQRCode(qrCode)}
                      className="flex-1 min-w-0 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      📥 {t('download')}
                    </button>
                    <button
                      onClick={() => printQRCode(qrCode)}
                      className="flex-1 min-w-0 px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      🖨️ {t('print')}
                    </button>
                    <button
                      onClick={() => copyQRCodeURL(qrCode)}
                      className="flex-1 min-w-0 px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      📋 {t('copyURL')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Invitations List */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('recentInvitations')}</h3>
        </div>

        {loadingInvitations ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('loadingInvitations')}</p>
          </div>
        ) : filteredInvitations.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8V4a1 1 0 00-1-1H7a1 1 0 00-1 1v1m16 0V4a1 1 0 00-1-1H7a1 1 0 00-1 1v1" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{t('noInvitations')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('getStartedBySendingYourFirstInvitation')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800 sticky left-0 z-20 border-r border-gray-200 dark:border-gray-700 w-24">
                    {t('Actions')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('email')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('role')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('createdBy')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('sent')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('expires')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredInvitations.map((invitation) => (
                  <tr key={invitation.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 group">
                    <td className="px-4 py-4 whitespace-nowrap bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800 sticky left-0 z-10 border-r border-gray-200 dark:border-gray-700 w-24">
                      {invitation.status === 'pending' && (
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => resendInvitation(invitation.id)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900"
                            title={t('resendInvitation')}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                          <button
                            onClick={() => cancelInvitation(invitation.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900"
                            title={t('cancelInvitation')}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {invitation.contactMethod && invitation.contactValue ? (
                        <div>
                          <div className="font-medium">{invitation.contactValue}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {invitation.contactMethod === 'email' && t('contactMethodEmail')}
                            {invitation.contactMethod === 'sms' && t('contactMethodSMS')}
                            {invitation.contactMethod === 'whatsapp' && t('contactMethodWhatsApp')}
                            {invitation.contactMethod === 'telegram' && t('contactMethodTelegram')}
                            {invitation.contactMethod === 'wechat' && t('contactMethodWeChat')}
                          </div>
                        </div>
                      ) : invitation.email ? (
                        <div>
                          <div className="font-medium">{invitation.email}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('contactMethodEmail')}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">{t('generic')}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {t(getRoleDisplayName(invitation.role))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(invitation.status)}>
                        {t(invitation.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {invitation.inviterFirstName && invitation.inviterLastName 
                        ? `${invitation.inviterFirstName} ${invitation.inviterLastName}`
                        : invitation.inviterEmail || 'Unknown'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDateTime(invitation.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDateTime(invitation.expiresAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmDialogShell
        isOpen={!!pendingCancelInviteId}
        onClose={() => !cancellingInvite && setPendingCancelInviteId(null)}
        onConfirm={confirmCancelInvitation}
        title={t('cancelInvitation')}
        confirmLabel={t('cancelInvitation')}
        confirmVariant="warning"
        confirmIcon={AlertTriangle}
        isLoading={cancellingInvite}
        isDisabled={cancellingInvite}
        showIcon={true}
        iconVariant="warning"
        closeOnBackdropClick={!cancellingInvite}
      >
        <p className={cn('text-sm', text.secondary)}>
          {t('Are you sure you want to cancel this invitation?')}
        </p>
      </ConfirmDialogShell>
    </div>
  );
};

export default InviteUsers;
